/**
 * The one Ketcher instance of the app (spec §8.9): mounted once, hidden when unused. Lazy-loaded
 * with React.lazy so the Ketcher/Indigo chunk loads on first edit only.
 * Verified against ketcher-react 3.18: `<Editor staticResourcesUrl structServiceProvider
 * errorHandler onInit(ketcher)>`; ketcher-core `Ketcher`: `getKet()`, `getMolfile("v3000")`,
 * `getRxn("v3000")`, `setMolecule(struct)`. A chemistry block saves `kind`/`ket`/`molfile`, a
 * reaction block `ket`/`rxn` (spec §8.9.2). A structure written by the MCP connector has no `ket` yet
 * (spec §10.9.3): the panel then loads the molfile or RXN, which `setMolecule` reads as well.
 */
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import type { Ketcher } from "ketcher-core";
import { closeChemPanel, useChemTarget } from "./panelStore";
import { canonicalSmiles, loadRDKit, splitRxn } from "./rdkit";
import { pruneCoefficients } from "../blocks/reaction/model";
import { Button } from "../ui/Button";
import { useT } from "../i18n";

const KetcherEditor = lazy(() => import("./KetcherEditor"));

declare global {
  interface Window {
    __kss?: { ketcher?: Ketcher };
  }
}

/** The structure to load: Ketcher's own format, else the molfile or RXN a connector write left. */
function structureOf(body: { get(key: string): unknown }): string {
  return String(body.get("ket") || body.get("molfile") || body.get("rxn") || "");
}

export function KetcherPanel() {
  const t = useT();
  const target = useChemTarget();
  const [ketcher, setKetcher] = useState<Ketcher | null>(null);
  const [remoteChanged, setRemoteChanged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const loadedFor = useRef<string | null>(null);

  // load the target's structure when the panel opens for a block
  useEffect(() => {
    if (!target || !ketcher) return;
    const key = `${target.handle.crdtDocId}:${target.treeId}`;
    loadedFor.current = key;
    void ketcher
      .setMolecule(structureOf(target.body))
      .catch(() => ketcher.setMolecule(""))
      .finally(() => setRemoteChanged(false));
    // remote change while open → banner (spec §8.9 item 4)
    const sub = target.body.subscribe((ev) => {
      if (ev.by !== "local") setRemoteChanged(true);
    });
    return () => sub();
  }, [target, ketcher]);

  const save = async () => {
    if (!target || !ketcher) return;
    setBusy(true);
    setSaveError(null);
    try {
      if (target.blockType === "reaction") {
        await saveReaction();
        return;
      }
      const ket = await ketcher.getKet();
      const parsed = JSON.parse(ket) as { root?: { nodes?: Array<{ type?: string }> } };
      const kind = parsed.root?.nodes?.some((n) => n.type === "arrow") ? "reaction" : "molecule";
      const molfile = kind === "reaction" ? await ketcher.getRxn("v3000") : await ketcher.getMolfile("v3000");
      // spec §7.4.8: kind, ket and molfile in one commit
      target.body.set("kind", kind);
      target.body.set("ket", ket);
      target.body.set("molfile", molfile);
      target.handle.doc.commit();
      setRemoteChanged(false);
    } finally {
      setBusy(false);
    }
  };
  /**
   * Spec §8.9.2 Save: `ket` and `rxn` in one commit, pruning coefficient overrides whose species is
   * no longer drawn. The KET is kept as-is (no `kind` decision): it is where arrow-side text would
   * come from later.
   */
  const saveReaction = async () => {
    if (!target || !ketcher) return;
    const ket = await ketcher.getKet();
    let rxn: string;
    try {
      rxn = await ketcher.getRxn("v3000");
    } catch {
      setSaveError(t("chem.needArrow"));
      return;
    }
    const rdkit = await loadRDKit();
    const { reactants, products, agents } = splitRxn(rxn);
    const drawn = new Set([...reactants, ...products, ...agents].map((m) => canonicalSmiles(rdkit, m)).filter((x): x is string => !!x));
    target.body.set("ket", ket);
    target.body.set("rxn", rxn);
    pruneCoefficients(target.body, drawn);
    target.handle.doc.commit();
    setRemoteChanged(false);
  };
  const reload = () => {
    if (!target || !ketcher) return;
    void ketcher.setMolecule(structureOf(target.body));
    setRemoteChanged(false);
  };

  return (
    <aside className={"fixed right-0 top-0 z-40 flex h-full w-[min(100vw,900px)] flex-col border-l border-neutral-200 bg-white shadow-xl dark:border-neutral-800 dark:bg-neutral-950 " + (target ? "" : "hidden")} data-testid="ketcher-panel" aria-hidden={!target}>
      <div className="flex items-center gap-2 border-b border-neutral-200 px-3 py-2 text-sm dark:border-neutral-800">
        <span className="font-medium">{target?.blockType === "reaction" ? t("chem.title.reaction") : t("chem.title.structure")}</span>
        {saveError && (
          <span className="text-xs text-red-600" data-testid="chem-save-error">
            {saveError}
          </span>
        )}
        <div className="grow" />
        {remoteChanged && (
          <span className="flex items-center gap-2 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900" data-testid="chem-remote-banner">
            {t("chem.changedRemotely")}
            <button className="underline" onClick={reload}>{t("chem.reloadRemote")}</button>
            <button className="underline" onClick={() => void save()}>{t("chem.overwrite")}</button>
          </span>
        )}
        <Button onClick={() => void save()} disabled={!ketcher || busy} data-testid="chem-save">{t("common.save")}</Button>
        <Button variant="ghost" onClick={closeChemPanel}>{t("common.close")}</Button>
      </div>
      <div className="min-h-0 grow">
        {target && (
          <Suspense fallback={<div className="p-4 text-sm text-neutral-500">{t("chem.loading")}</div>}>
            <KetcherEditor onInit={(k) => { setKetcher(k); window.__kss = { ...(window.__kss ?? {}), ketcher: k }; }} />
          </Suspense>
        )}
      </div>
    </aside>
  );
}
